// Запись касания: комментарий, звонок, встреча, письмо, сообщение. Одним
// сохранением — итог звонка, вложения, закрытие текущей задачи и следующий
// шаг: «позвонил — отметил — поставил, когда перезвонить».
import { CRM_ACTIVITY_KIND_LABELS, CRM_ACTIVITY_OUTCOMES, CRM_ACTIVITY_OUTCOME_LABELS, CRM_TASK_KINDS, CRM_TASK_KIND_LABELS, type CrmActivityInput, type CrmActivityKind, type CrmActivityOutcome, type CrmTask, type CrmTaskKind } from "@osmix/contracts";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Mail, MessageCircle, MessageSquare, Paperclip, Phone, Users, X, type LucideIcon } from "lucide-react";
import { type KeyboardEvent, useId, useState } from "react";
import { useAddActivity, useUploadFile } from "@/api/hooks/use-application-crm";
import { Segmented } from "@/components/segmented";
import { Select } from "@/components/ui/select";
import { DuePicker, presetDue } from "./due-picker";
import { errorText, fromLocalInput, guessFileCategory, toLocalInput } from "./format";
import { Chip, FieldError, Spinner, Textarea } from "./ui";
export const ACTIVITY_ICON: Record<CrmActivityKind, LucideIcon> = {
  comment: MessageSquare,
  call: Phone,
  meeting: Users,
  email: Mail,
  message: MessageCircle
};
const PLACEHOLDER: Record<CrmActivityKind, string> = {
  comment: "Заметка для себя и коллег",
  call: "О чём поговорили, что решили",
  meeting: "Итоги встречи, договорённости",
  email: "Что отправили или что ответили",
  message: "Что написали в мессенджере"
};

/** Каким касанием обычно закрывается задача этого вида — галочка ставится сама. */
const CLOSES: Partial<Record<CrmTaskKind, CrmActivityKind>> = {
  call: "call",
  meeting: "meeting",
  email: "email",
  send_offer: "email",
  message: "message"
};

/** Следующий шаг по итогу: «перезвонить» после недозвона, иначе — тем же каналом. */
const nextKindFor = (kind: CrmActivityKind, outcome: CrmActivityOutcome | null): CrmTaskKind => {
  if (outcome === "no_answer" || outcome === "callback") return "call";
  if (outcome === "interested") return "send_offer";
  if (kind === "comment") return "call";
  return kind;
};
export function ActivityComposer({
  applicationId,
  nextTask,
  openTasks
}: {
  applicationId: string;
  /** Ближайшая открытая задача — её можно закрыть этим касанием. */
  nextTask: CrmTask | null;
  openTasks: number;
}) {
  const fileInputId = useId();
  const add = useAddActivity(applicationId);
  const upload = useUploadFile(applicationId);
  const [kind, setKind] = useState<CrmActivityKind>("call");
  const [body, setBody] = useState("");
  const [outcome, setOutcome] = useState<CrmActivityOutcome | null>(null);
  // Пусто — «сейчас»: время подставится в момент сохранения.
  const [occurredAt, setOccurredAt] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [closeTask, setCloseTask] = useState<boolean | null>(null);
  const [nextOn, setNextOn] = useState<boolean | null>(null);
  const [nextKind, setNextKind] = useState<CrmTaskKind | null>(null);
  const [nextTitle, setNextTitle] = useState("");
  const [nextDue, setNextDue] = useState<string | null>(() => presetDue("tomorrow"));
  const [dueKey, setDueKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const withOutcome = kind === "call" || kind === "meeting";
  const closing = !!nextTask && (closeTask ?? CLOSES[nextTask.kind] === kind);
  // Шаг предлагаем, когда после этого касания у заявки не останется открытых задач.
  const nextEnabled = nextOn ?? (!nextTask || closing && openTasks <= 1);
  const effectiveNextKind = nextKind ?? nextKindFor(kind, outcome);
  const reset = () => {
    setBody("");
    setOutcome(null);
    setOccurredAt("");
    setFiles([]);
    setCloseTask(null);
    setNextOn(null);
    setNextKind(null);
    setNextTitle("");
    setNextDue(presetDue("tomorrow"));
    setDueKey(key => key + 1);
  };
  const submit = async () => {
    setError(null);
    if (kind === "comment" && !body.trim() && files.length === 0) {
      setError("Напишите комментарий");
      return;
    }
    if (withOutcome && !outcome && !body.trim()) {
      setError("Отметьте итог или опишите, о чём поговорили");
      return;
    }
    setBusy(true);
    try {
      const fileIds: string[] = [];
      for (const file of files) {
        const saved = await upload.mutateAsync({
          file,
          category: guessFileCategory(file)
        });
        fileIds.push(saved.id);
      }
      const input: CrmActivityInput = {
        kind,
        body: body.trim(),
        outcome: withOutcome ? outcome : null,
        occurredAt: kind === "comment" ? undefined : fromLocalInput(occurredAt) ?? undefined,
        fileIds,
        completeTaskId: closing ? nextTask?.id : undefined,
        nextTask: nextEnabled ? {
          title: nextTitle.trim() || CRM_TASK_KIND_LABELS[effectiveNextKind],
          kind: effectiveNextKind,
          dueAt: nextDue
        } : undefined
      };
      await add.mutateAsync(input);
      reset();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
    }
  };
  return <section className="min-w-0 rounded-xl border bg-card p-4 shadow-xs" aria-label="Новое касание" data-testid="crm-composer">
      <div className="flex flex-col gap-3">
        <Segmented value={kind} onChange={next => {
        setKind(next);
        setError(null);
        if (next !== "call" && next !== "meeting") setOutcome(null);
      }} options={(Object.keys(CRM_ACTIVITY_KIND_LABELS) as CrmActivityKind[]).map(value => ({
        value,
        label: CRM_ACTIVITY_KIND_LABELS[value],
        icon: ACTIVITY_ICON[value]
      }))} className="self-start" />

        <Textarea rows={3} value={body} aria-label="Текст касания" placeholder={PLACEHOLDER[kind]} disabled={busy} onChange={e_0 => setBody(e_0.target.value)} onKeyDown={onKeyDown} />

        {withOutcome && <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Итог">
            <span className="mr-1 text-xs text-muted-foreground">Итог</span>
            {CRM_ACTIVITY_OUTCOMES.map(value_0 => <Chip key={value_0} active={outcome === value_0} disabled={busy} className="px-2.5 text-xs" onClick={() => setOutcome(outcome === value_0 ? null : value_0)}>
                {CRM_ACTIVITY_OUTCOME_LABELS[value_0]}
              </Chip>)}
          </div>}

        <div className="flex flex-wrap items-center gap-2">
          {kind !== "comment" && <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Когда было
              <Input type="datetime-local" aria-label="Когда было" className="h-8 w-auto text-sm" value={occurredAt || toLocalInput(new Date().toISOString())} max={toLocalInput(new Date().toISOString())} disabled={busy} onChange={e_1 => setOccurredAt(e_1.target.value)} />
            </label>}
          <label htmlFor={fileInputId} className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <Paperclip className="size-4" />
            Прикрепить
          </label>
          <input id={fileInputId} type="file" multiple className="sr-only" onChange={event_0 => {
          const picked = Array.from(event_0.target.files ?? []);
          event_0.target.value = "";
          setFiles(current => [...current, ...picked]);
        }} />
          {files.map((file_0, index) => <span key={`${file_0.name}-${index}`} className="inline-flex max-w-full items-center gap-1 rounded-full border bg-muted/40 py-0.5 pr-1 pl-2.5 text-xs">
              <span className="max-w-48 truncate">{file_0.name}</span>
              <button type="button" aria-label={`Убрать ${file_0.name}`} className="rounded-full p-0.5 text-muted-foreground hover:text-foreground" onClick={() => setFiles(current_0 => current_0.filter((_, i) => i !== index))}>
                <X className="size-3" />
              </button>
            </span>)}
        </div>

        {nextTask && <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5 size-4 accent-primary" checked={closing} disabled={busy} onChange={e_2 => setCloseTask(e_2.target.checked)} />
            <span className="min-w-0">
              Закрыть задачу «<span className="font-medium break-words">{nextTask.title}</span>»
            </span>
          </label>}

        <div className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="size-4 accent-primary" checked={nextEnabled} disabled={busy} onChange={e_3 => setNextOn(e_3.target.checked)} />
            Следующий шаг
          </label>
          {nextEnabled && <>
              <div className="grid gap-2 sm:grid-cols-[11rem_minmax(0,1fr)]">
                <Select aria-label="Вид следующего шага" className="h-8" value={effectiveNextKind} disabled={busy} onChange={e_4 => setNextKind(e_4.target.value as CrmTaskKind)}>
                  {CRM_TASK_KINDS.map(value_1 => <option key={value_1} value={value_1}>
                      {CRM_TASK_KIND_LABELS[value_1]}
                    </option>)}
                </Select>
                <Input aria-label="Что сделать" className="h-8" placeholder={CRM_TASK_KIND_LABELS[effectiveNextKind]} value={nextTitle} disabled={busy} onChange={e_5 => setNextTitle(e_5.target.value)} />
              </div>
              <DuePicker key={dueKey} value={nextDue} onChange={setNextDue} disabled={busy} />
            </>}
        </div>

        <FieldError>{error}</FieldError>
        <div className="flex items-center justify-end gap-3">
          <span className="hidden text-xs text-muted-foreground sm:inline">Ctrl + Enter</span>
          <Button disabled={busy} onClick={() => void submit()}>
            {busy && <Spinner />}
            Сохранить
          </Button>
        </div>
      </div>
    </section>;
}